import * as Api from '@ViewModels';
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { css, StyleSheet } from 'aphrodite';
import * as React from 'react';
import ActionItemsPlaceholderIconUrl from '../../../assets/actionItemsListPlaceholderIcon.svg';
import { Button } from '../../../components-v2/Button';
import { Skeleton } from '../../../components-v2/Skeleton';
import { THEME } from '../../../components-v2/Theme/theme';
import { getActionItemTitle } from '../utils/actionItemText';
import { ActionItemBucket, BUCKET_ORDER, DROPPABLE_BUCKETS, GroupedActionItems } from '../utils/buckets';
import { formatDueLabel } from '../utils/formatDue';
import { ACTION_ITEM_DRAG_TYPE, ActionItemRow, IActionItemRowCallbacks } from './ActionItemRow';
import { ActionItemSection } from './ActionItemSection';

const COLLAPSED_STORAGE_KEY = 'actionItems.collapsedBuckets';

type CollapsedState = Partial<Record<ActionItemBucket, boolean>>;

function readCollapsed(): CollapsedState {
	try {
		const raw = window.localStorage.getItem(COLLAPSED_STORAGE_KEY);
		return raw ? (JSON.parse(raw) as CollapsedState) : {};
	} catch {
		return {};
	}
}

function writeCollapsed(value: CollapsedState) {
	try {
		window.localStorage.setItem(COLLAPSED_STORAGE_KEY, JSON.stringify(value));
	} catch {
		// Collapsed sections are a convenience; losing them is fine.
	}
}

const NEXT_UP_ORDER: ActionItemBucket[] = ['tomorrow', 'thisWeek', 'later', 'noDate'];

export function nextUpActionItem(groups: GroupedActionItems): Api.IActionItem | null {
	for (const bucket of NEXT_UP_ORDER) {
		if (groups[bucket].length > 0) {
			return groups[bucket][0];
		}
	}
	return null;
}

interface IProps extends IActionItemRowCallbacks {
	groups: GroupedActionItems;
	isLoading: boolean;
	/** A new scope or search is loading while the previous results stay on screen. */
	isRefreshing?: boolean;
	hasMore: boolean;
	selectedIds: Set<string>;
	onToggleSelected: (id: string, withRange: boolean) => void;
	onDropToBucket: (id: string, bucket: ActionItemBucket) => void;
	onAddToBucket: (bucket: ActionItemBucket) => void;
}

export const ActionItemGroups: React.FC<IProps> = ({
	groups,
	isLoading,
	isRefreshing = false,
	hasMore,
	selectedIds,
	onToggleSelected,
	onDropToBucket,
	onAddToBucket,
	...rowCallbacks
}) => {
	const [collapsed, setCollapsed] = React.useState<CollapsedState>(readCollapsed);
	const [dragging, setDragging] = React.useState(false);
	const onDropToBucketRef = React.useRef(onDropToBucket);
	React.useLayoutEffect(() => {
		onDropToBucketRef.current = onDropToBucket;
	});

	React.useEffect(() => {
		return monitorForElements({
			canMonitor: ({ source }) => source.data.type === ACTION_ITEM_DRAG_TYPE,
			onDragStart: () => setDragging(true),
			onDrop: ({ source, location }) => {
				setDragging(false);
				const target = location.current.dropTargets[0];
				const bucket = target?.data.bucket as ActionItemBucket | undefined;
				const id = source.data.id as string | undefined;
				if (bucket && id) {
					onDropToBucketRef.current(id, bucket);
				}
			},
		});
	}, []);

	const toggleCollapsed = (bucket: ActionItemBucket) => {
		setCollapsed(previous => {
			const next = { ...previous, [bucket]: !previous[bucket] };
			writeCollapsed(next);
			return next;
		});
	};

	const total = BUCKET_ORDER.reduce((sum, bucket) => sum + groups[bucket].length, 0);
	const selectionActive = selectedIds.size > 0;

	if (isLoading) {
		return (
			<div className={css(styleSheet.skeletons)} data-testid='action-items-loading'>
				{[0, 1, 2, 3, 4].map(index => (
					<Skeleton key={index} styles={[styleSheet.skeletonRow]} />
				))}
			</div>
		);
	}

	const refreshing = isRefreshing ? (
		<div className={css(styleSheet.refreshing)} role='status' aria-live='polite' data-testid='action-items-refreshing'>
			<span className={css(styleSheet.refreshingTrack)}>
				<span className={css(styleSheet.refreshingBar)} />
			</span>
			<span>Loading action items…</span>
		</div>
	) : null;

	if (total === 0) {
		return (
			<div className={css(styleSheet.empty)} data-testid='action-items-empty'>
				{refreshing}
				<img className={css(styleSheet.emptyArt)} src={ActionItemsPlaceholderIconUrl} alt='' />
				<span className={css(styleSheet.emptyTitle)}>You’re all caught up</span>
				<span className={css(styleSheet.emptySubtitle)}>
					Add an action item above, or press <kbd className={css(styleSheet.kbd)}>n</kbd> anywhere on this page. Type{' '}
					<kbd className={css(styleSheet.kbd)}>@</kbd> to reference a contact or company and{' '}
					<kbd className={css(styleSheet.kbd)}>for @</kbd> to assign a teammate.
				</span>
			</div>
		);
	}

	const nextUp = groups.today.length === 0 && groups.overdue.length === 0 ? nextUpActionItem(groups) : null;
	let rowIndex = 0;

	return (
		<div className={css(styleSheet.container, isRefreshing && styleSheet.containerRefreshing)}>
			{refreshing}
			{BUCKET_ORDER.map(bucket => {
				const items = groups[bucket];
				const droppable = DROPPABLE_BUCKETS.includes(bucket);
				const visible = items.length > 0 || bucket === 'today' || (dragging && droppable);
				if (!visible) {
					return null;
				}
				return (
					<ActionItemSection
						key={bucket}
						bucket={bucket}
						count={items.length}
						collapsed={!!collapsed[bucket]}
						onToggleCollapsed={() => toggleCollapsed(bucket)}
						droppable={droppable}
						dragging={dragging}
						onAdd={bucket === 'overdue' ? undefined : () => onAddToBucket(bucket)}
					>
						{bucket === 'today' && items.length === 0 && !dragging ? (
							<div className={css(styleSheet.clear)} data-testid='today-clear'>
								<img className={css(styleSheet.clearArt)} src={ActionItemsPlaceholderIconUrl} alt='' />
								<div className={css(styleSheet.clearText)}>
									<span className={css(styleSheet.clearTitle)}>You’re clear for today</span>
									{nextUp ? (
										<span className={css(styleSheet.clearSubtitle)}>
											<span>Next up:</span>
											<Button
												variant='link'
												size='sm'
												styles={[styleSheet.nextUpButton]}
												onClick={() => rowCallbacks.onOpen(nextUp)}
											>
												{getActionItemTitle(nextUp)}
											</Button>
											{nextUp.dueDate ? (
												<span>· {formatDueLabel(new Date(nextUp.dueDate), !!nextUp.hasDueTime)}</span>
											) : null}
										</span>
									) : (
										<span className={css(styleSheet.clearSubtitle)}>
											Press <kbd className={css(styleSheet.kbd)}>n</kbd> to add something for today.
										</span>
									)}
								</div>
							</div>
						) : (
							items.map(item => (
								<ActionItemRow
									key={item.id}
									index={rowIndex++}
									item={item}
									selected={selectedIds.has(item.id)}
									selectionActive={selectionActive}
									onToggleSelected={onToggleSelected}
									{...rowCallbacks}
								/>
							))
						)}
					</ActionItemSection>
				);
			})}
			{hasMore ? (
				<div className={css(styleSheet.hasMore)}>
					Showing the first {total} action items. Narrow the search or scope to see the rest.
				</div>
			) : null}
		</div>
	);
};

const slide = {
	'0%': { transform: 'translateX(-100%)' },
	'100%': { transform: 'translateX(250%)' },
};

const styleSheet = StyleSheet.create({
	refreshing: {
		alignItems: 'center',
		color: THEME.colors.text.light,
		display: 'flex',
		fontSize: THEME.typography.fontSizes.xs,
		gap: THEME.spacing.sm,
		padding: `${THEME.spacing.xs}px 0`,
	},
	refreshingTrack: {
		backgroundColor: THEME.colors.primary.light,
		borderRadius: 2,
		display: 'block',
		flex: 1,
		height: 3,
		overflow: 'hidden',
		position: 'relative',
	},
	refreshingBar: {
		animationDuration: '1.1s',
		animationIterationCount: 'infinite',
		animationName: [slide],
		animationTimingFunction: 'ease-in-out',
		backgroundColor: THEME.colors.primary.main,
		borderRadius: 2,
		display: 'block',
		height: '100%',
		width: '40%',
	},
	containerRefreshing: {
		opacity: 0.6,
		transition: 'opacity 150ms ease-out',
	},
	container: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.xs,
	},
	skeletons: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.sm,
		padding: THEME.spacing.sm,
	},
	skeletonRow: {
		height: 52,
		borderRadius: THEME.common.borderRadius,
	},
	empty: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		gap: THEME.spacing.sm,
		padding: THEME.spacing.xxxl,
		textAlign: 'center',
		backgroundColor: THEME.colors.background.light,
		border: THEME.common.border,
		borderRadius: THEME.common.borderRadius,
	},
	emptyArt: {
		width: 72,
		height: 72,
		marginBottom: THEME.spacing.sm,
	},
	emptyTitle: {
		fontFamily: THEME.typography.fontFamilies.sora,
		fontSize: THEME.typography.fontSizes.lg,
		fontWeight: THEME.typography.fontWeights.bold,
		color: THEME.colors.text.main,
	},
	emptySubtitle: {
		fontSize: THEME.typography.fontSizes.sm,
		color: THEME.colors.text.light,
		maxWidth: 480,
		lineHeight: 1.5,
	},
	kbd: {
		border: THEME.common.border,
		borderRadius: THEME.common.borderRadiusSm,
		padding: '0 6px',
		fontFamily: 'inherit',
		fontSize: THEME.typography.fontSizes.xs,
		backgroundColor: THEME.colors.background.light,
	},
	clear: {
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.md,
		padding: `${THEME.spacing.md}px ${THEME.spacing.lg}px`,
		backgroundColor: THEME.colors.background.light,
		border: THEME.common.border,
		borderRadius: THEME.common.borderRadius,
	},
	clearArt: {
		width: 48,
		height: 48,
		flexShrink: 0,
	},
	clearText: {
		display: 'flex',
		flexDirection: 'column',
		gap: 2,
		minWidth: 0,
	},
	clearTitle: {
		fontFamily: THEME.typography.fontFamilies.sora,
		fontSize: THEME.typography.fontSizes.md,
		fontWeight: THEME.typography.fontWeights.bold,
		color: THEME.colors.text.main,
	},
	clearSubtitle: {
		alignItems: 'baseline',
		color: THEME.colors.text.light,
		display: 'inline-flex',
		flexWrap: 'wrap',
		fontSize: THEME.typography.fontSizes.sm,
		gap: THEME.spacing.xs,
	},
	nextUpButton: {
		display: 'inline',
		fontSize: THEME.typography.fontSizes.sm,
		height: 'auto',
		padding: 0,
		verticalAlign: 'baseline',
		whiteSpace: 'nowrap',
	},
	hasMore: {
		padding: THEME.spacing.md,
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.text.light,
		textAlign: 'center',
	},
});
